// frontend/modules/template/views/TemplateManagementView.tsx
import React, { useState } from 'react';
import { Card, Typography, Button, Input, Space, Select, Modal, Empty, Divider } from 'antd';
import { PlusOutlined, ReloadOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import { useTemplates } from '../hooks/useTemplates';
import TemplateList from '../components/TemplateList';
import TemplateForm from '../components/TemplateForm';
import TemplateImportExport from '../components/TemplateImportExport';
import { NotificationPlatforms } from '@/shared/api/model';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;
const { confirm } = Modal;

const TemplateManagementView: React.FC = () => {
    const { dark } = useTheme();
    const {
        filteredTemplates,
        isLoading,
        searchTerm,
        selectedPlatform,

        setSearchTerm,
        setSelectedPlatform,
        loadTemplates,
        deleteTemplate,
        editTemplate,
        resetActiveTemplate,
        exportTemplates,
        importTemplates
    } = useTemplates();

    const [isFormVisible, setIsFormVisible] = useState(false);

    const handleCreateNew = () => {
        resetActiveTemplate();
        setIsFormVisible(true);
    };

    const handleEditTemplate = (name: string, platform: NotificationPlatforms) => {
        // @ts-ignore
        editTemplate(name, platform, null);
        setIsFormVisible(true);
    };

    const handleDeleteTemplate = (name: string, platform: NotificationPlatforms) => {
        confirm({
            title: 'Are you sure you want to delete this template?',
            icon: <ExclamationCircleOutlined />,
            content: 'This will delete all language versions of this template.',
            okText: 'Yes, delete it',
            okType: 'danger',
            cancelText: 'No',
            onOk() {
                deleteTemplate(name, platform);
            }
        });
    };

    return (
        <div className="space-y-6">
            <Card
                className={dark ? 'bg-gray-800 text-white' : ''}
                title={
                    <div className="flex justify-between items-center">
                        <Title level={4} className={dark ? 'text-white mb-0' : 'mb-0'}>
                            Message Templates
                        </Title>
                        <Space>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={() => loadTemplates()}
                                loading={isLoading}
                            >
                                Refresh
                            </Button>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={handleCreateNew}
                            >
                                Create Template
                            </Button>
                        </Space>
                    </div>
                }
            >
                <div className="flex flex-wrap gap-4 justify-between mb-6">
                    <div className="flex flex-wrap gap-4">
                        <Search
                            placeholder="Search by template name"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ width: 300 }}
                            allowClear
                        />

                        <Select
                            placeholder="Filter by platform"
                            value={selectedPlatform}
                            onChange={setSelectedPlatform}
                            style={{ width: 200 }}
                            allowClear
                        >
                            <Option value={NotificationPlatforms.WHATS_APP}>WhatsApp</Option>
                            <Option value={NotificationPlatforms.SMS}>SMS</Option>
                            <Option value={NotificationPlatforms.EMAIL}>Email</Option>
                            <Option value={NotificationPlatforms.IN_APP}>In-App</Option>
                        </Select>
                    </div>

                    <TemplateImportExport
                        onExport={exportTemplates}
                        onImport={importTemplates}
                        isLoading={isLoading}
                    />
                </div>

                <Divider className={dark ? 'border-gray-700' : ''} />

                {filteredTemplates.length > 0 ? (
                    <TemplateList
                        onEdit={handleEditTemplate}
                        onDelete={handleDeleteTemplate}
                    />
                ) : (
                    <Empty
                        description={
                            <span className={dark ? 'text-gray-300' : ''}>
                {isLoading ? 'Loading templates...' : 'No templates found'}
              </span>
                        }
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                )}
            </Card>

            <TemplateForm
                visible={isFormVisible}
                onCancel={() => setIsFormVisible(false)}
            />
        </div>
    );
};

export default TemplateManagementView;